import { useAtomValue } from 'jotai'
import uniqBy from 'lodash/uniqBy'
import React from 'react'
import { useTranslation } from 'react-i18next'
import useTokens from 'src/common/apollo/hooks/useTokens'
import { OffersQueryVariables } from 'src/common/apollo/types/swarmx/graphql'
import useToggle from 'src/common/hooks/helpers/useToggle'
import Badge from 'src/common/ui-kit/Badge'
import BadgeGroup from 'src/common/ui-kit/BadgeGroup'
import Button from 'src/common/ui-kit/Button/Button'
import Checkbox from 'src/common/ui-kit/Checkbox/Checkbox'
import Drawer from 'src/common/ui-kit/Drawer'
import Option from 'src/common/ui-kit/Select/Option'
import Select from 'src/common/ui-kit/Select/Select'
import TokenIcon from 'src/common/ui-kit/TokenIcon/TokenIcon'
import { activeTabAtom } from 'src/pages/DiscoverPage/discover-store'
import {
  ExpandedOfferFillType,
  Filters,
  OfferFillTypeOptions,
  TimeRangeOptions,
} from '../filters'
import useManageFilter from '../hooks/useManageFilter'
import { filterOptions } from '../utils'
import './styles.css'
import useCurrentAssetValues from '../hooks/useCurrentAssetValues'

export interface OffersFilterProps {
  onFilterChange: (filters: OffersQueryVariables['filter']) => void
}

const OffersFilterMobile: React.FC<OffersFilterProps> = ({
  onFilterChange,
}) => {
  const { isOn, off, on } = useToggle()

  const { erc20s, nfts } = useTokens()
  const { t } = useTranslation('offersManagement', {
    keyPrefix: 'offersFilter',
  })

  const {
    filters,
    applyFilter,
    updateOfferFillTypeFilter,
    updateTimeRangeFilter,
    resetFilters,
  } = useManageFilter(onFilterChange)

  const {
    currentFilterHandlers,
    currentRenderedValue,
    currentAssetOptionKey,
    currentFilters,
  } = useCurrentAssetValues(onFilterChange)

  const activeTab = useAtomValue(activeTabAtom)

  const filteredOfferFillTypeOptions = filterOptions(
    OfferFillTypeOptions,
    activeTab
  )

  const assets = [...erc20s, ...uniqBy(nfts, 'contractAddress')]

  const assetFilterOptions = assets.map(item => ({
    value: item.contractAddress,
    label: item.symbol,
  }))

  const getAssetOption = (
    value: string,
    label: string,
    key: keyof Pick<Filters, 'depositAssets' | 'withdrawalAssets'>
  ) => {
    return (
      <Option className='flex flex-row items-center' key={value} value={value}>
        <div className='flex items-center justify-between w-full'>
          <div className='flex items-center'>
            <TokenIcon
              token={assets.find(item => item.contractAddress === value)}
            />
            <div className='mx-2'>{label}</div>
          </div>
          <Checkbox
            className='ml-auto'
            checked={filters[key].toString().includes(value)}
          />
        </div>
      </Option>
    )
  }

  const handleApplyFilter = () => {
    applyFilter()
    off()
  }

  const handleResetFilter = () => {
    resetFilters()
    off()
  }

  return (
    <>
      <div className='comp-offer-filter-mobile-actions'>
        <Button className='w-full px-4' onClick={on}>
          {t('actions.open')}
        </Button>
        <Button
          className='w-full px-4'
          variant='outlined'
          onClick={resetFilters}
        >
          {t('actions.reset')}
        </Button>
      </div>

      <Drawer open={isOn} onClose={off}>
        <div className='comp-offer-filter-mobile'>
          <h4 className='comp-offer-title'>{t('title')}</h4>

          <div className='comp-offer-filter-assets'>
            <div className='comp-offer-filter-item'>
              <span className='comp-offer-filter-title'>
                {t('withdrawalAssets.label')}
              </span>
              <Select<string>
                multiselect
                className='comp-offer-filter-select'
                value={currentFilters.sell}
                onChange={currentFilterHandlers.sell}
                placeholder={t('withdrawalAssets.placeholder')}
                renderedValue={currentRenderedValue.sell}
              >
                {assetFilterOptions.map(({ value, label }) =>
                  getAssetOption(value, label, currentAssetOptionKey.sell)
                )}
              </Select>
            </div>

            <div className='comp-offer-filter-item'>
              <span className='comp-offer-filter-title'>
                {t('depositAssets.label')}
              </span>
              <Select<string>
                multiselect
                className='comp-offer-filter-select'
                value={currentFilters.buy}
                onChange={currentFilterHandlers.buy}
                placeholder={t('depositAssets.placeholder')}
                renderedValue={currentRenderedValue.buy}
              >
                {assetFilterOptions.map(({ value, label }) =>
                  getAssetOption(value, label, currentAssetOptionKey.buy)
                )}
              </Select>
            </div>
          </div>

          <div className='divider' />

          <div className='comp-offer-filter-item'>
            <span className='comp-offer-filter-title'>
              {t('offerStatus.label')}
            </span>
            <BadgeGroup<ExpandedOfferFillType>
              onChange={updateOfferFillTypeFilter}
              value={filters.offerStatuses}
              multiselect
              className='flex-wrap items-center'
            >
              {filteredOfferFillTypeOptions.map(({ value, label }) => (
                <Badge
                  key={value}
                  value={value}
                  color={
                    filters.offerStatuses.includes(value)
                      ? 'primary'
                      : 'secondary'
                  }
                  className='flex items-center gap-2'
                  size='lg'
                >
                  <div>{label}</div>
                </Badge>
              ))}
            </BadgeGroup>
          </div>
          <div className='divider' />

          <div className='comp-offer-filter-item'>
            <span className='comp-offer-filter-title'>
              {t('timeRange.label')}
            </span>

            <div className='flex gap-2'>
              {TimeRangeOptions.map(({ value, label }) => (
                <Badge
                  key={value}
                  color='secondary'
                  active={value === filters.timeRange}
                  onClick={() => updateTimeRangeFilter(value)}
                >
                  {label}
                </Badge>
              ))}
            </div>
          </div>
          <div className='divider' />

          <div className='comp-offer-filter-actions'>
            <Button
              onClick={handleApplyFilter}
              className='comp-offer-filter-action w-full'
              variant='contained'
            >
              {t('actions.apply')}
            </Button>

            <Button
              onClick={handleResetFilter}
              className='comp-offer-filter-action w-full'
              variant='outlined'
            >
              {t('actions.reset')}
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  )
}

export default OffersFilterMobile
